import { useContext, useMemo } from 'react';
import type { ThemeOptions } from '@mui/material/styles';
import WLContext from '@/labels/context';
import { useSelector } from 'react-redux';
import { rtlEnable } from '@/domain/env';

const useDefaultThemeConfig = (): ThemeOptions => {
  const {
    labelOptions: { styles: wlThemeStyles, ...wlThemeConfig },
  } = useContext(WLContext);
  const isRtl = useSelector(rtlEnable);

  return useMemo(
    (): ThemeOptions => ({
      ...wlThemeStyles,
      palette: {
        ...wlThemeStyles.palette,
        // Chat-only off-white (#FBFDFF) surface for the conversation right side
        // (message list + input + empty state), excluding the white header.
        // Fixed value, not white-label-brandable. Consumed by EmptyPanelContent
        // (via theme) and mapped to the `--dokka-chat-surface` CSS var in
        // ChatProvider's colorSet for the Sendbird `.sendbird-conversation` element.
        chat: {
          ...wlThemeStyles.palette?.chat,
          surface: 'rgba(251, 253, 255, 1)',
        },
      },
      zIndex: {
        // Above toasts (tooltip, 1500), so a toast never covers the open chat.
        chatDrawer: 1600,
        // Tooltips, menus and dialogs opened from the chat, above its drawer.
        chatPopup: 1700,
        // Covers the whole application, above every interactive layer.
        screenLock: 1800,
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            '*': {
              // [MUI] - needs for JE after migrate to mui
              margin: 0,
              padding: 0,
              fontFamily: ['Rubik', 'Helvetica', 'Arial', 'sans-serif'].join(','),
            },
            body: {
              direction: 'ltr',
            },
            '#root': {
              minHeight: '100vh',
              display: 'flex',
            },
            '.UserSelect-none': {
              userSelect: 'none',
            },
            // hide widget launcher iframe via css, because sometimes freshdesk widget dont want hide through
            // window.FreshworksWidget('hide', 'launcher');
            // I guess Freshdesk script is not yet initialized when hide API is invoked
            // 1 - always hide using styles/css
            // 2- try hide use widget API after 1second timeout - see HelpDropdown component
            '#launcher-frame': {
              display: 'none',
            },
          },
        },
        MuiButtonGroup: {
          defaultProps: {
            disableElevation: true,
          },
          styleOverrides: {
            root: {
              borderRadius: 100,
            },
          },
        },
        MuiButton: {
          defaultProps: {
            variant: 'contained',
            disableElevation: true,
          },
          styleOverrides: {
            root: {
              borderRadius: 100,
            },
          },
        },
        // TODO: check behaviour - having stuck tooltips for disabled IconButtons
        // cant use code below
        // MuiIconButton: {
        //   styleOverrides: {
        //     root: {
        //       '&[disabled]': {
        //         cursor: 'not-allowed',
        //         pointerEvents: 'auto',
        //       },
        //     },
        //   },
        // },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 8,
            },
          },
        },
        // DA-19422: one tab height for the whole app — Figma 20954-39493 is a 42px bar
        // (40px row + the 2px indicator sitting on the divider). MUI's 48px comes from
        // `padding: 12px 16px` on top of the `button` line box (14px x 1.75 = 24.5px), NOT
        // from `minHeight` — which is why the per-screen `minHeight: 40` overrides that
        // predate this were inert. A small vertical padding hands the height back to
        // `minHeight` (which then sets it exactly) while still leaving room if a label wraps.
        // Both slots are required: Tabs owns the strip's box, Tab each item's.
        MuiTabs: {
          styleOverrides: {
            root: { minHeight: 40 },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: ({ theme }) => ({
              minHeight: 40,
              padding: theme.spacing(0.5, 2),
            }),
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: ({ theme }) => ({
              backgroundColor: theme.palette.grey[800],
              padding: theme.spacing(0.25, 1),
            }),
            arrow: ({ theme }) => ({
              color: theme.palette.grey[800],
            }),
          },
        },
      },
      direction: isRtl ? 'rtl' : 'ltr',
      typography: {
        fontFamily: ['Rubik', 'Helvetica', 'Arial', 'sans-serif'].join(','),
        fontWeightSemiBold: 600,
      },
      mixins: {
        toolbar: {
          minHeight: 64,
        },
      },
      labelOptions: wlThemeConfig,
    }),
    [isRtl, wlThemeConfig, wlThemeStyles],
  );
};

export default useDefaultThemeConfig;
